import { action, computed, observable } from 'mobx';

import { SerializedData } from 'bernie-core';
import { Store } from 'bernie-plugin-mobx';

import {
  FlightsShoppingPwaFlightSearchResultsFlightsSearch,
  FlightsShoppingPwaFlightInformationResults,
  FlightsDisplayComponentCountingValue
} from '__generated__/api';
import NoListingMessaging = FlightsShoppingPwaFlightSearchResultsFlightsSearch.NoListingMessaging;
import FailedRequestMessaging = FlightsShoppingPwaFlightSearchResultsFlightsSearch.FailedRequestMessaging;
import ServerErrorMessaging = FlightsShoppingPwaFlightInformationResults.ServerErrorMessaging;
import { ServicingCompleteBookingFailure } from 'src/components/servicing';

export class ErrorStore extends Store {
  @observable public clientFetchFailure: boolean;
  @observable public noListingMessaging: NoListingMessaging;
  @observable public failedRequestMessaging: FailedRequestMessaging;
  @observable public serverErrorMessaging: ServerErrorMessaging;
  @observable public noApiResponse: boolean;
  @observable public redirectToStorefront: boolean;
  @observable public flightsDisplayComponentCountingValue: FlightsDisplayComponentCountingValue.Fragment;
  @observable public multiItemError: boolean;
  @observable public backTrackError: boolean;
  @observable public showEmptyStateError: boolean;
  @observable public showErrorSummary: boolean;
  @observable public showPriceChangeError: boolean;
  @observable public failedServicingMessaging: ServicingCompleteBookingFailure;

  @computed get shouldRenderNFFMessage(): boolean {
    const isRecognizedNoListingsMessagingError =
      (this.noListingMessaging?.__typename === 'FlightsNoFlightsFoundMessaging' ||
        this.noListingMessaging?.__typename === 'FlightsNoFlightsFoundWithFilterMessaging') &&
      !this.shouldRenderSmartNFF;

    return !this.clientFetchFailure && this.noListingMessaging && isRecognizedNoListingsMessagingError;
  }

  @computed get shouldRenderSmartNFF(): boolean {
    const isNFFSmartMessaging = 'FlightsSmartNoFlightsFoundMessaging' === this.noListingMessaging?.__typename;

    return !this.clientFetchFailure && this.noListingMessaging && isNFFSmartMessaging;
  }

  @computed get shouldRenderFailedRequestError(): boolean {
    const renderFailedRequestErrorFSR =
      (this.clientFetchFailure || this.noListingMessaging) &&
      this.failedRequestMessaging &&
      !this.shouldRenderNFFMessage &&
      !this.shouldRenderSmartNFF;
    const renderFailedRequestErrorFIS = this.serverErrorMessaging?.__typename === 'FlightsActionableErrorMessaging';

    return renderFailedRequestErrorFSR || renderFailedRequestErrorFIS;
  }

  @computed get shouldRenderNoApiResponseError(): boolean {
    return (
      !this.shouldRenderFailedRequestError &&
      !this.shouldRenderNFFMessage &&
      (this.noApiResponse || this.clientFetchFailure)
    );
  }

  @computed get shouldRenderServicingFailedRequestError(): boolean {
    return this.failedServicingMessaging?.emptyState && this.showEmptyStateError && !this.clientFetchFailure;
  }

  @computed get shouldRenderRecoverableErrorSummary(): boolean {
    return this.failedServicingMessaging?.errorSummary && this.showErrorSummary;
  }

  @computed get shouldRenderRecoverablePriceChangeError(): boolean {
    return this.failedServicingMessaging?.placards && this.showPriceChangeError;
  }

  @action
  public setMultiItemError(multiItemError: boolean) {
    this.multiItemError = multiItemError;
  }

  @action
  public setClientFetchFailure(clientFetchFailure: boolean) {
    this.clientFetchFailure = clientFetchFailure;
  }

  @action
  public setEmptyStateError(showEmptyStateError: boolean) {
    this.showEmptyStateError = showEmptyStateError;
  }

  @action
  public setErrorSummary(showErrorSummary: boolean) {
    this.showErrorSummary = showErrorSummary;
  }

  @action
  public setPriceChangeError(showPriceChangeError: boolean) {
    this.showPriceChangeError = showPriceChangeError;
  }

  @action
  public setBackTrackError(backTrackError: boolean) {
    this.backTrackError = backTrackError;
  }

  public setDisplayComponentCountingValue(
    flightsDisplayComponentCountingValue: FlightsDisplayComponentCountingValue.Fragment
  ) {
    this.flightsDisplayComponentCountingValue = flightsDisplayComponentCountingValue;
  }

  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
  }

  public reset(): void {
    this.clientFetchFailure = false;
    this.noListingMessaging = null;
  }
}

ErrorStore.DEFAULTS = {
  clientFetchFailure: false,
  noListingMessaging: null,
  failedRequestMessaging: null,
  noApiResponse: true,
  redirectToStorefront: false,
  multiItemError: false,
  backTrackError: false,
  showEmptyStateError: false,
  showErrorSummary: false,
  showPriceChangeError: false,
  failedServicingMessaging: null
};
